/**
 * Událost do kalendáře ve formátu iCalendar (.ics) – otevře ji kalendář v iPhonu,
 * Androidu, Outlooku i Google Kalendáři. Čas je „plovoucí“ (místní čas telefonu).
 */

export interface IcsEvent {
  uid: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM */
  time: string;
  durationMin: number;
  title: string;
  description?: string;
  url?: string;
  /** Upozornění kolik dní předem (0 = v čas události). */
  alarmDaysBefore?: number | null;
  yearly?: boolean;
}

const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Řádky delší než 75 bajtů se podle RFC 5545 lámou (pokračování začíná mezerou). */
function fold(line: string): string {
  const enc = new TextEncoder();
  if (enc.encode(line).length <= 75) return line;
  const out: string[] = [];
  let cur = '';
  for (const ch of line) {
    if (enc.encode(cur + ch).length > (out.length ? 74 : 75)) {
      out.push(cur);
      cur = ch;
    } else cur += ch;
  }
  out.push(cur);
  return out.join('\r\n ');
}

const stamp = (d: string, t: string) => `${d.replace(/-/g, '')}T${t.replace(':', '')}00`;

function addMinutes(date: string, time: string, min: number): [string, string] {
  const d = new Date(`${date}T${time}:00Z`);
  d.setUTCMinutes(d.getUTCMinutes() + min);
  const iso = d.toISOString();
  return [iso.slice(0, 10), iso.slice(11, 16)];
}

export function icsCalendar(e: IcsEvent, now = new Date()): string {
  const [endD, endT] = addMinutes(e.date, e.time, e.durationMin);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Ulnik//CS',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${e.uid}`,
    `DTSTAMP:${now.toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`,
    `DTSTART:${stamp(e.date, e.time)}`,
    `DTEND:${stamp(endD, endT)}`,
    ...(e.yearly ? ['RRULE:FREQ=YEARLY'] : []),
    `SUMMARY:${esc(e.title)}`,
    ...(e.description ? [`DESCRIPTION:${esc(e.description)}`] : []),
    ...(e.url ? [`URL:${e.url}`] : []),
    ...(e.alarmDaysBefore != null
      ? [
          'BEGIN:VALARM',
          'ACTION:DISPLAY',
          `TRIGGER:${e.alarmDaysBefore > 0 ? `-P${e.alarmDaysBefore}D` : 'PT0M'}`,
          `DESCRIPTION:${esc(e.title)}`,
          'END:VALARM',
        ]
      : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.map(fold).join('\r\n') + '\r\n';
}

/** Odkaz „přidat do Google Kalendáře“ (bez souboru). */
export function googleCalendarUrl(e: IcsEvent): string {
  const [endD, endT] = addMinutes(e.date, e.time, e.durationMin);
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${stamp(e.date, e.time)}/${stamp(endD, endT)}`,
    details: [e.description, e.url].filter(Boolean).join('\n\n'),
  });
  if (e.yearly) p.set('recur', 'RRULE:FREQ=YEARLY');
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}
